import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, Signal, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLinkWithHref } from '@angular/router';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonInput,
  IonRouterLinkWithHref,
  IonRow,
  LoadingController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginLogoComponent, ScreensizeService } from '@okcargo-apps/layout-ionic';
import { EMessageLevel, MessagesUiService } from '@okcargo-apps/messages-ui';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { detailedEmailValidator } from '@okcargo-apps/utils';
import { Subscription, catchError, finalize, throwError } from 'rxjs';
import { AccountService } from '../../account';
import { AuthService } from '../../auth';

@Component({
  selector: 'lib-password-recover-request',
  templateUrl: './password-recover-request.component.html',
  styleUrls: ['./password-recover-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    IonRouterLinkWithHref,
    RouterLinkWithHref,
    IonButton,
    IonInput,
    IonCol,
    IonRow,
    IonGrid,
    TranslateModule,
    LoginLogoComponent,
  ],
})
export class PasswordRecoverRequestComponent implements OnInit, OnDestroy {
  private readonly accountService = inject(AccountService);
  private readonly authService = inject(AuthService);
  private readonly connectivity = inject(ConnectivityService);

  /** A password operation is a command: it cannot be done without connection. */
  protected readonly isOffline = this.connectivity.isOffline;
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly screensizeService = inject(ScreensizeService);

  public changePasswordRequestForm: FormGroup;

  public account = this.accountService.account;
  public isDesktopView: Signal<boolean> = this.screensizeService.isDesktopView;

  public errorMessage = signal<string | null>(null);

  public changePasswordRequestHelperText: string = this.translate.instant('account.change_password_request_helper');
  public changePasswordRequestErrorText: string = this.translate.instant('error.invalid_email_format');

  private statusChangesSubscription!: Subscription;

  // TODO modify this component / service.
  /*
  Cuando el usuario envíe una solicitud de recuperación de contraseña no debería devolver el backend si ese usuario
  existe o no existe en el sistema (motivos de seguridad). Siempre debería mostrarse la misma respuesta:
  "Solicitud de recuperación de contraseña enviada. Si la dirección de correo electrónico corresponde a un usuario
  registrado en okcargo recibirá un email con las intrucciones para la recuperación de la contraseña."
  No se debería navegar a otra página solo en alguno casos: o bien siempre se le redirige a la pantalla para introducir el código
  o bien siempre permanece en esta misma pantalla / componente. Posiblemente la segunda opción sea la más segura (si recibe el email
  siempre tendrá el enlace a la pantalla para introducir el código)

   */

  constructor() {
    this.changePasswordRequestForm = new FormGroup({
      email: new FormControl('', [Validators.required, detailedEmailValidator()]),
    });
  }

  ngOnInit(): void {
    this.statusChangesSubscription = this.changePasswordRequestForm.statusChanges.subscribe(() => {
      this.printErrors();
    });
  }

  ngOnDestroy(): void {
    this.statusChangesSubscription.unsubscribe();
  }

  public async changePasswordRequest(): Promise<void> {
    if (this.isOffline()) return;

    if (!this.changePasswordRequestForm.valid) {
      this.printErrors(true);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    await loading.present();

    const { email } = this.changePasswordRequestForm.value;

    this.authService
      .changePasswordRequest(email)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // console.log('Error detail:', error.error);
          if (error.status === 400) {
            this.errorMessage.set(this.translate.instant('error.old_password_ko'));
          } else {
            this.errorMessage.set(this.translate.instant('error.unknown'));
          }
          return throwError(() => error);
        }),
        finalize(() => loading.dismiss()),
      )
      .subscribe({
        next: () => {
          this.messagesUiService.setMessage({
            objectId: 'account',
            message: [this.translate.instant('account.password_change_requested')],
            level: EMessageLevel.INFO,
          });
          this.router.navigate(['/recover-password-confirm']);
        },
      });
  }

  private printErrors(markAsTouched = false): void {
    const newPasswordControl = this.changePasswordRequestForm.get('newPassword');
    const newPasswordRepeatControl = this.changePasswordRequestForm.get('newPasswordRepeat');
  }
}
